import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, TrendingUp, TrendingDown, Minus, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "GymSales Match Rate" KPI card for /intelligence.
 *
 * Answers the practical ops question: of the revivable deals my team has
 * actually tried to coach on, how many gave us real CRM history to work
 * with? A high match rate = playbooks are personalised. A low rate = the
 * team is coaching in the dark and CRM hygiene needs attention.
 *
 * Sparkline shows the last 7 days of daily match rate so the manager
 * can spot a sudden drop (e.g. GymSales API credential rot).
 */
const GymSalesMatchRateCard = ({ domainId = null, days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rematching, setRematching] = useState(false);
  const [runStatus, setRunStatus] = useState(null);

  const load = (cancelledRef) => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', String(days));
    return axios
      .get(`${API}/analytics/gymsales/match-rate?${p.toString()}`)
      .then((r) => { if (!cancelledRef?.cancelled) setData(r.data); })
      .catch(() => { if (!cancelledRef?.cancelled) toast.error('Could not load GymSales match rate.'); });
  };

  useEffect(() => {
    const ref = { cancelled: false };
    setLoading(true);
    load(ref).finally(() => { if (!ref.cancelled) setLoading(false); });
    return () => { ref.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, days]);

  const rematch = async () => {
    setRematching(true);
    try {
      const p = new URLSearchParams();
      if (domainId) p.set('domain_id', domainId);
      p.set('days', String(days));
      const r = await axios.post(`${API}/analytics/gymsales/match-rate/rehydrate?${p.toString()}`);
      if (r.data.already_running) {
        toast.info('A re-match is already running — results will appear shortly.');
      } else {
        toast.info(`Re-match started — checking ${r.data.scanned} unmatched deal${r.data.scanned === 1 ? '' : 's'} against GymSales…`);
      }
      // Poll the run until it finishes (CRM lookups can take a while).
      const sp = new URLSearchParams();
      if (domainId) sp.set('domain_id', domainId);
      for (let i = 0; i < 200; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 3000));
        // eslint-disable-next-line no-await-in-loop
        const s = await axios.get(`${API}/analytics/gymsales/match-rate/rehydrate/status?${sp.toString()}`);
        setRunStatus(s.data);
        if (s.data.status === 'done') {
          const d = s.data;
          if (d.now_matched > 0) {
            toast.success(`Re-match complete: ${d.now_matched} of ${d.rechecked} now matched a GymSales contact`);
          } else if (d.lookup_failures > 0) {
            toast.error(`Re-match ran but ${d.lookup_failures} lookups failed — GymSales API may be unreachable`);
          } else {
            toast.info(`Re-checked ${d.rechecked} deals — still no CRM match (check phone formats in GymSales)`);
          }
          break;
        }
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-match failed');
    }
    setRematching(false);
    setRunStatus(null);
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading match rate…
      </div>
    );
  }
  if (!data || data.enabled === false) return null;

  const rate = data.match_rate_pct ?? 0;
  const total = data.total_hydrated_lookups || 0;
  const matched = data.matched || 0;
  const series = data.series_7d || [];
  const weekly = data.series_weekly || [];

  // Trend arrow — compare last day's rate to the 7d average.
  const dailyRates = series.map((s) => s.rate).filter((r) => r != null);
  const avg = dailyRates.length ? dailyRates.reduce((a, b) => a + b, 0) / dailyRates.length : null;
  const latest = series.length ? series[series.length - 1].rate : null;
  const trend = latest != null && avg != null ? (latest - avg) : 0;
  const TrendIcon = trend > 5 ? TrendingUp : trend < -5 ? TrendingDown : Minus;
  const trendTone = trend > 5 ? 'text-emerald-600' : trend < -5 ? 'text-rose-600' : 'text-slate-500';

  // Card tone bands mirror the sparkline colour so the manager sees
  // status at a glance. ≥60% good; 40-59 amber; <40 rose.
  const tone = rate >= 60
    ? 'border-emerald-200 bg-emerald-50'
    : rate >= 40
    ? 'border-amber-200 bg-amber-50'
    : 'border-rose-200 bg-rose-50';
  const numTone = rate >= 60 ? 'text-emerald-700' : rate >= 40 ? 'text-amber-700' : 'text-rose-700';

  // Sparkline geometry — 90 wide × 24 tall.
  const W = 90, H = 24;
  const maxRate = Math.max(100, ...dailyRates);
  const pts = series.length
    ? series
        .map((s, i) => {
          const x = (i / Math.max(1, series.length - 1)) * W;
          const y = s.rate == null ? H : H - (s.rate / maxRate) * H;
          return `${x},${y}`;
        })
        .join(' ')
    : '';

  return (
    <div
      className={`rounded-xl border ${tone} p-4 flex flex-col gap-2 min-w-[220px]`}
      data-testid="gymsales-match-rate-card"
    >
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-slate-600" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
          GymSales Match Rate
        </p>
        <span className="ml-auto text-[10px] text-slate-500">last {days}d</span>
      </div>
      <div className="flex items-end gap-3">
        <p className={`text-3xl font-bold tabular-nums ${numTone}`} data-testid="gymsales-match-rate-value">
          {rate.toFixed(1)}%
        </p>
        <div className={`flex items-center gap-1 text-[11px] font-semibold ${trendTone}`}>
          <TrendIcon className="w-3 h-3" />
          {trend > 0 ? '+' : ''}{trend.toFixed(1)} vs 7d avg
        </div>
      </div>
      <p className="text-[11px] text-slate-600">
        <span className="font-semibold tabular-nums">{matched}</span> of{' '}
        <span className="font-semibold tabular-nums">{total}</span> coached revivable
        deals matched a GymSales contact
      </p>
      {series.length > 0 && (
        <div className="flex items-center gap-2 mt-1">
          <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="text-slate-400">
            <polyline
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              points={pts}
            />
          </svg>
          <span className="text-[10px] text-slate-500 tabular-nums">
            {series.filter((s) => s.rate != null).length}/{series.length} days with data
          </span>
        </div>
      )}
      {weekly.length > 0 && (
        <div className="mt-1 border-t border-slate-200/60 pt-1.5" data-testid="gymsales-weekly-trend">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
            Weekly matched leads (8 wks)
          </p>
          <div className="flex items-end gap-1 h-10">
            {weekly.map((w) => {
              const max = Math.max(1, ...weekly.map((x) => x.matched));
              const h = Math.max(2, Math.round((w.matched / max) * 30));
              return (
                <div
                  key={w.week_start}
                  className="flex-1 flex flex-col items-center gap-0.5"
                  title={`Week of ${w.week_start}: ${w.matched} matched of ${w.hydrated} coached${w.rate != null ? ` (${w.rate}%)` : ''}`}
                  data-testid={`gymsales-week-${w.week_start}`}
                >
                  <span className="text-[9px] text-slate-500 tabular-nums leading-none">{w.matched}</span>
                  <div
                    className="w-full rounded-sm bg-teal-500/80"
                    style={{ height: `${h}px`, opacity: w.hydrated ? 1 : 0.25 }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] text-slate-400 mt-0.5">
            <span>{weekly[0].week_start.slice(5)}</span>
            <span>this week</span>
          </div>
        </div>
      )}
      {rate < 40 && total >= 10 && (
        <p className="text-[10px] text-rose-700 italic leading-snug border-t border-rose-100 pt-1.5 mt-1">
          Low match rate — check that customer_phone numbers are being captured
          in the right format (Yabbit stores E.164; GymSales expects the same).
        </p>
      )}
      {(data.unmatched || 0) > 0 && (
        <div className="border-t border-slate-200/60 pt-1.5 mt-1" data-testid="gymsales-rematch-section">
          {data.unmatched_reasons && Object.keys(data.unmatched_reasons).length > 0 && (
            <p className="text-[10px] text-slate-500 mb-1">
              Unmatched: {Object.entries(data.unmatched_reasons)
                .map(([k, v]) => `${v} ${k === 'lookup_failed' ? 'lookup failed (API issue)' : k === 'no_credentials' ? 'no credentials' : k === 'no_crm_match' ? 'no CRM match' : k}`)
                .join(' · ')}
            </p>
          )}
          <button
            onClick={rematch}
            disabled={rematching}
            className="w-full text-[11px] font-bold px-3 py-1.5 rounded-full bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
            data-testid="gymsales-rematch-btn"
          >
            {rematching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {rematching
              ? (runStatus?.status === 'running'
                ? `Re-matching… ${runStatus.rechecked || 0}/${runStatus.scanned || '?'} checked, ${runStatus.now_matched || 0} matched`
                : 'Re-matching…')
              : `Re-match ${data.unmatched} unmatched now`}
          </button>
          <p className="text-[9px] text-slate-400 mt-1 leading-snug">
            Re-runs the CRM lookup for unmatched deals — recovers matches lost
            to API outages. No AI cost.
          </p>
        </div>
      )}
    </div>
  );
};

export default GymSalesMatchRateCard;
